// src/constants/categories.js
//
// One place for every label shown to users (audit FL-13, FL-19, FA-04, FA-35).
// Mirrors backend/app/utils/categories.py. Values are stored in the DB as codes
// (e.g. HIP_HOP_MAINSTREAM / JUNIOR / BASIC); never render the codes directly.

export const REGULAR_DISCIPLINES = [
  'ART_DANCE',
  'DISCO_DANCE',
  'FUSION_DANCE',
  'FUSION_SHOW',
  'HIP_HOP_MAINSTREAM',
  'HIP_HOP_UNDERGROUND',
];
export const SENIOR_DISCIPLINES = ['SENIOR_DANCE', 'SENIOR_SHOW'];
export const DISCIPLINES = [...REGULAR_DISCIPLINES, ...SENIOR_DISCIPLINES];
export const AGE_CATEGORIES = ['BABY', 'CHILDREN', 'JUNIOR', 'ADULT', 'SENIOR'];
export const LEAGUES = ['HOBBY', 'BASIC', 'PROFESSIONAL'];

export const DISCIPLINE_LABELS = {
  ART_DANCE: 'Art Dance',
  DISCO_DANCE: 'Disco Dance',
  FUSION_DANCE: 'Fusion Dance',
  FUSION_SHOW: 'Fusion Show',
  HIP_HOP_MAINSTREAM: 'Hip Hop Mainstream',
  HIP_HOP_UNDERGROUND: 'Hip Hop Underground',
  SENIOR_DANCE: 'Senior Dance',
  SENIOR_SHOW: 'Senior Show',
  FREESTYLE: 'Freestyle', // 2025 season, kept for old entries
};

export const AGE_CATEGORY_LABELS = {
  BABY: 'Baby',
  CHILDREN: 'Děti',
  JUNIOR: 'Junioři',
  ADULT: 'Dospělí',
  SENIOR: 'Senioři',
};

export const LEAGUE_LABELS = {
  HOBBY: 'Hobby',
  BASIC: 'Základní',
  PROFESSIONAL: 'Profi',
  // legacy spellings
  'ZÁKLADNÍ': 'Základní',
  PROFI: 'Profi',
};

// Choreography.status
export const CHOREOGRAPHY_STATUS = {
  pre_registered: {
    label: 'Předregistrace',
    hint: 'Registrace ještě není otevřena. Po jejím otevření choreografii zaregistrujte.',
    tone: 'purple',
  },
  draft: {
    // Same word as the instructions on bestofthebest.cz ("PŘIHLÁŠEN = oficiálně zaregistrováno").
    label: 'Přihlášena',
    hint: 'Choreografie je zaregistrovaná. Nahrajte hudbu a potvrďte prezenci.',
    tone: 'amber',
  },
  confirmed: {
    label: 'Prezence potvrzena',
    hint: 'Choreografie je závazně přihlášena.',
    tone: 'green',
  },
  approved: { label: 'Schváleno', hint: 'Choreografie byla schválena organizátorem.', tone: 'green' },
  rejected: { label: 'Zamítnuto', hint: 'Kontaktujte prosím organizátory.', tone: 'red' },
};

// Competition.status
export const COMPETITION_STATUS = {
  upcoming: { label: 'Připravovaná', tone: 'blue' },
  active: { label: 'Registrace otevřena', tone: 'green' },
  closed: { label: 'Registrace uzavřena', tone: 'gray' },
  finished: { label: 'Ukončená', tone: 'slate' },
  archived: { label: 'Archivovaná', tone: 'slate' },
};

export const COMPETITION_TYPE_LABELS = {
  regional: 'Semifinále',
  final: 'Finále',
  grand_finale: 'Grand Finále',
};

// Choreography.payment_status / Invoice.status
export const PAYMENT_STATUS = {
  pending: { label: 'Čeká na fakturu', tone: 'amber' },
  invoicing: { label: 'Vystavuje se faktura', tone: 'blue' },
  invoiced: { label: 'Vyfakturováno', tone: 'blue' },
  paid: { label: 'Zaplaceno', tone: 'green' },
  waived: { label: 'Bez poplatku', tone: 'gray' },
};

export const INVOICE_STATUS = {
  issued: { label: 'Čeká na úhradu', tone: 'amber' },
  paid: { label: 'Zaplaceno', tone: 'green' },
  cancelled: { label: 'Stornováno', tone: 'gray' },
};

// Choreography.verification_status
export const VERIFICATION_STATUS = {
  approved: { label: 'Ověřeno', tone: 'green' },
  change_needed: { label: 'Navržena změna', tone: 'red' },
};

export const disciplineLabel = (code) => DISCIPLINE_LABELS[code] || code || '';
export const ageCategoryLabel = (code) => AGE_CATEGORY_LABELS[code] || code || '';
export const leagueLabel = (code) => LEAGUE_LABELS[code] || code || '';
export const choreographyStatusLabel = (code) => CHOREOGRAPHY_STATUS[code]?.label || code || '';
export const competitionStatusLabel = (code) => COMPETITION_STATUS[code]?.label || code || '';

/** "Hip Hop Mainstream · Junioři · Základní" */
export const categoryLabel = (discipline, ageCategory, league) =>
  [disciplineLabel(discipline), ageCategoryLabel(ageCategory), leagueLabel(league)].filter(Boolean).join(' · ');

/** The 62 valid (discipline, ageCategory, league) combinations - same rules as the backend. */
export const ALL_VALID_COMBINATIONS = AGE_CATEGORIES.flatMap((age) => {
  if (age === 'SENIOR') return SENIOR_DISCIPLINES.map((d) => ({ discipline: d, ageCategory: age, league: 'BASIC' }));
  if (age === 'BABY') return REGULAR_DISCIPLINES.map((d) => ({ discipline: d, ageCategory: age, league: 'HOBBY' }));
  return REGULAR_DISCIPLINES.flatMap((d) => LEAGUES.map((league) => ({ discipline: d, ageCategory: age, league })));
});
